/**
 * TauntSystem — pure, framework-free, frame-driven taunt state machine for
 * the player character.
 *
 * Risk/reward design (MY-1959):
 *   - Taunting during a safe window heals a small amount of HP and charges
 *     the star meter — but only if the taunt completes uninterrupted.
 *   - Taking a hit mid-taunt cancels immediately and forfeits all remaining
 *     reward (no partial heal/charge accrual beyond what was already applied).
 *   - A cooldown prevents immediately re-taunting.
 *
 * All timing constants are tunable via constructor so the system is fully
 * unit-testable without touching the singleton defaults.
 *
 * Deliberately imports NOTHING — no Phaser, no other systems.
 */

// ─── Tunables ──────────────────────────────────────────────────────────────────

/** Frames the taunt animation plays at 60 fps (~1.5 s). */
export const TAUNT_DURATION_FRAMES = 90;

/** Frames before the player can taunt again after one completes. (~5 s). */
export const TAUNT_COOLDOWN_FRAMES = 300;

/**
 * Maximum HP (as fraction of max HP, 0..1) that can be healed over a full
 * uninterrupted taunt. The actual amount healed is proportional to the
 * fraction of the taunt that completes.
 *
 * e.g. 0.05 = 5 % of maxHp healed total.
 */
export const TAUNT_HEAL_FRACTION = 0.05;

/**
 * Maximum star meter charge that can be added over a full uninterrupted taunt.
 * Fractional values are accumulated and floored before being returned to the
 * caller; the caller applies them as integer star additions.
 */
export const TAUNT_STAR_CHARGE = 1.0;

/** Number of discrete pose indices the taunt cycles through (0..POSE_COUNT-1). */
export const TAUNT_POSE_COUNT = 5;

// ─── State ─────────────────────────────────────────────────────────────────────

export interface TauntSystemState {
  /** Frames remaining in the current taunt (0 = not taunting). */
  activeFrames: number;
  /** Frames remaining on the cooldown (0 = can taunt). */
  cooldownFrames: number;
  /** Accumulated fractional star charge for current taunt invocation. */
  starAccum: number;
  /** Accumulated fractional HP heal for current taunt invocation. */
  healAccum: number;
}

export function createTauntState(): TauntSystemState {
  return { activeFrames: 0, cooldownFrames: 0, starAccum: 0, healAccum: 0 };
}

// ─── Per-frame output ──────────────────────────────────────────────────────────

export interface TauntTickResult {
  /** HP to heal this frame (integer, ≥ 0). */
  healDelta: number;
  /**
   * Whole stars to add to the meter this frame (integer, ≥ 0).
   * The system accumulates fractional charge internally and only releases
   * whole units so the caller's integer star counter stays accurate.
   */
  starDelta: number;
  /** Pose index to display (0..TAUNT_POSE_COUNT-1). */
  poseIndex: number;
  /** Whether the taunt is still active after this tick. */
  stillActive: boolean;
}

// ─── TauntSystem ───────────────────────────────────────────────────────────────

export class TauntSystem {
  private readonly durationFrames: number;
  private readonly cooldownFrames: number;
  private readonly healFraction: number;
  private readonly starCharge: number;

  private state: TauntSystemState = createTauntState();

  constructor(opts?: {
    durationFrames?: number;
    cooldownFrames?: number;
    healFraction?: number;
    starCharge?: number;
  }) {
    this.durationFrames = opts?.durationFrames ?? TAUNT_DURATION_FRAMES;
    this.cooldownFrames = opts?.cooldownFrames ?? TAUNT_COOLDOWN_FRAMES;
    this.healFraction = opts?.healFraction ?? TAUNT_HEAL_FRACTION;
    this.starCharge = opts?.starCharge ?? TAUNT_STAR_CHARGE;
  }

  // ─── Read-only accessors ──────────────────────────────────────────────────

  get isActive(): boolean {
    return this.state.activeFrames > 0;
  }

  get isOnCooldown(): boolean {
    return this.state.cooldownFrames > 0;
  }

  /**
   * Current pose index (0..TAUNT_POSE_COUNT-1), computed from how far through
   * the taunt animation we are.
   */
  get poseIndex(): number {
    if (!this.isActive) return 0;
    const elapsed = this.durationFrames - this.state.activeFrames;
    return Math.floor((elapsed / this.durationFrames) * TAUNT_POSE_COUNT) % TAUNT_POSE_COUNT;
  }

  // ─── Eligibility ─────────────────────────────────────────────────────────

  /**
   * Returns true when the player is allowed to start a taunt.
   *
   * @param grounded  Player must be on the ground.
   * @param busy      True when in hitstun, attack recovery, throw, or airborne.
   */
  canStart(grounded: boolean, busy: boolean): boolean {
    return grounded && !busy && !this.isActive && !this.isOnCooldown;
  }

  // ─── Commands ────────────────────────────────────────────────────────────

  /** Begin a taunt. Call only when canStart() is true. */
  start(): void {
    this.state.activeFrames = this.durationFrames;
    this.state.cooldownFrames = 0;
    this.state.starAccum = 0;
    this.state.healAccum = 0;
  }

  /**
   * Advance the taunt one frame while active. Returns per-frame reward deltas.
   * The heal delta is scaled to maxHp by the caller.
   *
   * @param maxHp  Player's maximum HP — used to scale the per-frame heal.
   */
  tick(maxHp: number): TauntTickResult {
    if (!this.isActive) {
      return { healDelta: 0, starDelta: 0, poseIndex: 0, stillActive: false };
    }

    // Accumulate fractional heal; release whole HP units each frame.
    const totalHeal = maxHp * this.healFraction;
    this.state.healAccum += totalHeal / this.durationFrames;
    const healDelta = Math.floor(this.state.healAccum);
    this.state.healAccum -= healDelta;

    // Accumulate fractional star charge; release whole stars each frame.
    this.state.starAccum += this.starCharge / this.durationFrames;
    const starDelta = Math.floor(this.state.starAccum);
    this.state.starAccum -= starDelta;

    const poseIndex = this.poseIndex;

    this.state.activeFrames -= 1;

    const stillActive = this.state.activeFrames > 0;
    let finalHealDelta = healDelta;
    let finalStarDelta = starDelta;
    if (!stillActive) {
      // Taunt completed naturally — flush any remaining fractional amounts so
      // floating-point drift doesn't silently drop the full reward.
      finalHealDelta += Math.round(this.state.healAccum);
      finalStarDelta += Math.round(this.state.starAccum);
      this.state.healAccum = 0;
      this.state.starAccum = 0;
      this.state.cooldownFrames = this.cooldownFrames;
    }

    return { healDelta: finalHealDelta, starDelta: finalStarDelta, poseIndex, stillActive };
  }

  /**
   * Cancel the taunt immediately (called when the player takes a hit).
   * Forfeits all remaining reward and starts the cooldown.
   */
  cancel(): void {
    if (!this.isActive) return;
    this.state.activeFrames = 0;
    this.state.starAccum = 0;
    this.state.healAccum = 0;
    this.state.cooldownFrames = this.cooldownFrames;
  }

  /**
   * Tick the cooldown down one frame when NOT taunting.
   * Call each game-frame from Player.update() regardless of taunt state so
   * the cooldown ticks even while the player is doing other things.
   */
  tickCooldown(): void {
    if (this.state.cooldownFrames > 0) {
      this.state.cooldownFrames -= 1;
    }
  }

  // ─── Snapshot (for tests / save state) ───────────────────────────────────

  getState(): Readonly<TauntSystemState> {
    return { ...this.state };
  }
}
